/** Max length for CRF action item vReplyRemark (matches API). */
export const REPLY_REMARK_MAX_LENGTH = 2000;

/**
 * Header line pattern: "Display (login)(profile) dd-MMM-yyyy HH:mm" (server-stamped appends).
 */
const HEADER_LINE_RE =
  /^(.+)\s+\(([^)]+)\)\(([^)]+)\)\s+(\d{1,2}-[A-Za-z]{3}-\d{4})\s+(\d{1,2}:\d{2})\s*$/;

/**
 * @param {string|undefined|null} raw
 * @returns {Array<{ key: string, displayName: string, login: string, profile: string, datePart: string, timePart: string, headerLine: string, body: string, isLegacy?: boolean }>}
 */
export function parseReplyRemarkToMessages(raw) {
  const text = (raw ?? '').toString();
  if (!text.trim()) return [];

  const lines = text.split(/\r?\n/);
  const messages = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const m = HEADER_LINE_RE.exec(line);
    if (m) {
      const displayName = m[1].trim();
      const login = m[2].trim();
      const profile = m[3].trim();
      const datePart = m[4];
      const timePart = m[5];
      i += 1;
      const bodyLines = [];
      while (i < lines.length && !HEADER_LINE_RE.test(lines[i])) {
        bodyLines.push(lines[i]);
        i += 1;
      }
      const body = bodyLines.join('\n').replace(/\s+$/, '');
      messages.push({
        key: `${messages.length}-${login}-${datePart}-${timePart}`,
        displayName,
        login,
        profile,
        datePart,
        timePart,
        headerLine: line,
        body,
      });
      while (i < lines.length && lines[i].trim() === '') i += 1;
    } else {
      const start = i;
      const chunk = [];
      while (i < lines.length && !HEADER_LINE_RE.test(lines[i])) {
        chunk.push(lines[i]);
        i += 1;
      }
      const legacy = chunk.join('\n').trim();
      if (legacy) {
        messages.push({
          key: `legacy-${start}`,
          displayName: '',
          login: '',
          profile: '',
          datePart: '',
          timePart: '',
          headerLine: '',
          body: legacy,
          isLegacy: true,
        });
      }
    }
  }

  return messages;
}

/**
 * For field audit Old/New columns: show only message bodies, not stamped
 * "Name (login)(profile) dd-MMM-yyyy HH:mm" lines (Performed By / time columns cover that).
 * @param {string|undefined|null} value raw vReplyRemark snapshot from audit
 * @returns {string}
 */
export function formatReplyRemarkPlainForAuditDisplay(value) {
  if (value == null) return '';
  const full = String(value).trim();
  if (!full) return '';

  const messages = parseReplyRemarkToMessages(full);
  if (messages.length > 0) {
    const parts = messages.map((m) => (m.body || '').trim()).filter(Boolean);
    if (parts.length > 0) return parts.join('\n\n');
  }

  const lines = full.split(/\r?\n/);
  const first = (lines[0] || '').trim();
  if (lines.length >= 2 && HEADER_LINE_RE.test(first)) {
    return lines.slice(1).join('\n').trim();
  }

  return full;
}

/**
 * For Closed issues, treat the last stamped message body as the closure remark.
 * Returns empty string when no parseable message exists.
 * @param {string|undefined|null} raw
 * @returns {string}
 */
export function getClosureRemarkFromReplyRemark(raw) {
  const messages = parseReplyRemarkToMessages(raw);
  if (messages.length === 0) return '';
  const last = messages[messages.length - 1];
  return (last?.body || '').toString().trim();
}

/**
 * Removes the last stamped message block from vReplyRemark (used to hide closure remark from
 * Description of Action while still preserving earlier message history).
 * @param {string|undefined|null} raw
 * @returns {string}
 */
export function stripClosureRemarkFromReplyRemark(raw) {
  const full = (raw ?? '').toString();
  if (!full.trim()) return '';
  const messages = parseReplyRemarkToMessages(full);
  if (messages.length === 0) return full.trim();
  const last = messages[messages.length - 1];
  if (!last || last.isLegacy || !last.headerLine) return full.trim();

  const lines = full.split(/\r?\n/);
  let lastHeaderIdx = -1;
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    if (lines[i] === last.headerLine) {
      lastHeaderIdx = i;
      break;
    }
  }
  if (lastHeaderIdx < 0) return full.trim();

  const kept = lines.slice(0, lastHeaderIdx).join('\n').replace(/\s+$/, '');
  return kept;
}

/**
 * @param {{ login?: string, isLegacy?: boolean }} msg
 * @param {object|null|undefined} user normalized session user
 */
export function isMessageFromCurrentUser(msg, user) {
  if (!msg || msg.isLegacy || !msg.login) return false;
  const u = (user?.userName || user?.UserName || user?.vUserName || user?.VUserName || '')
    .toString()
    .trim()
    .toLowerCase();
  if (!u) return false;
  return msg.login.trim().toLowerCase() === u;
}

/**
 * Preview line similar to server RecordedSign (for composer hint only).
 * @param {object|null|undefined} user
 */
export function getCurrentUserReplySignature(user) {
  if (!user || typeof user !== 'object') return '';
  const fn = (user.firstName || user.FirstName || '').toString().trim();
  const ln = (user.lastName || user.LastName || '').toString().trim();
  let firstLast = `${fn} ${ln}`.trim();
  const uname = (user.userName || user.UserName || user.vUserName || user.VUserName || '')
    .toString()
    .trim();
  if (!firstLast) firstLast = uname || 'User';
  const profile = (
    user.profileDescription
    || user.ProfileDescription
    || user.profileCode
    || user.ProfileCode
    || ''
  )
    .toString()
    .trim() || 'No Profile';
  const uDisp = uname || '—';
  return `${firstLast} (${uDisp})(${profile})`;
}

/**
 * Soft budget for new message body so merged string likely stays under API cap (header is built server-side).
 * @param {string} persistedRaw
 */
export function getComposerMaxLength(persistedRaw) {
  const p = (persistedRaw ?? '').toString().length;
  return Math.max(0, REPLY_REMARK_MAX_LENGTH - p - 160);
}
